import {use, useEffect, useRef} from 'react'
import {announce} from '@github-ui/aria-live'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {ContentPreviewContext} from '@github-ui/copilot-immersive-v1/components/ContentPreview/ContentPreviewContext'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {File} from '@github-ui/copilot-immersive-v1/components/ContentPreview/content-preview-types'

import type {Artifact} from '../../utils/artifacts/artifact-core'
import {
  buildArtifactHtmlPreview,
  cacheArtifactHtmlPreview,
  type ArtifactHtmlPreviewCacheEntry,
  type ArtifactHtmlPreviewPlan,
} from '../../utils/artifacts/html-preview-plan'
import {createArtifactHtmlPreview} from './ArtifactHtmlPreview'

/**
 * Sentinel the workbench uses for previewable content that no chat message
 * produced. Duplicated from `@github-ui/copilot-chat`'s `NullMessageId` rather
 * than depending on that package for a single string.
 */
const NULL_MESSAGE_ID = 'NULL_MESSAGE'
const ARTIFACT_PREVIEW_ITEM_PREFIX = 'file:artifact:'
const EMPTY_READABLE_REF_PATHS = new Set<string>()
const EMPTY_ARTIFACT_CATALOG: readonly Artifact[] = []

export const NO_PREVIEW_REASON = 'No preview available for this file.'

export function useOptionalArtifactContentPreview() {
  return use(ContentPreviewContext)
}

export function getArtifactPreviewItemId(artifact: Artifact): File['id'] {
  return `${ARTIFACT_PREVIEW_ITEM_PREFIX}${artifact.absolutePath}#0`
}

function isArtifactPreviewItemId(id: string): id is File['id'] {
  return id.startsWith(ARTIFACT_PREVIEW_ITEM_PREFIX)
}

function isHtmlArtifact(artifact: Artifact): boolean {
  return artifact.language ? artifact.language.toLowerCase() === 'html' : /\.html?$/i.test(artifact.displayPath)
}

function htmlPreviewFields(
  plan: ArtifactHtmlPreviewPlan | undefined,
  id: File['id'],
): Pick<File, 'previewEligible' | 'htmlPreview'> {
  return {
    previewEligible:
      plan?.kind === 'legacy' ? plan.previewEligible : plan === undefined ? undefined : plan.kind === 'prepared',
    htmlPreview: plan?.kind === 'prepared' ? createArtifactHtmlPreview(plan, id) : undefined,
  }
}

export function buildArtifactPreviewItem(
  artifact: Artifact,
  timestamp: Date,
  initialViewMode?: File['initialViewMode'],
  preview?: {catalog: readonly Artifact[]; writesSettled: boolean},
): File | undefined {
  if (artifact.content === undefined) return undefined
  const plan = isHtmlArtifact(artifact)
    ? buildArtifactHtmlPreview(artifact, preview?.catalog ?? EMPTY_ARTIFACT_CATALOG, preview?.writesSettled ?? false)
    : undefined

  return {
    type: 'file',
    // Derived from the path so reopening the same artifact reuses its tab.
    id: getArtifactPreviewItemId(artifact),
    // The file came from a tool call, not a chat message, so there is no
    // message to anchor it to. Matches how the workbench is driven from
    // reference clicks elsewhere.
    messageId: NULL_MESSAGE_ID,
    name: artifact.displayPath,
    language: artifact.language,
    value: artifact.content,
    isStreaming: false,
    isUserEdited: false,
    readOnly: true,
    ...htmlPreviewFields(plan, getArtifactPreviewItemId(artifact)),
    timestamp,
    ...(initialViewMode === undefined ? {} : {initialViewMode}),
  }
}

export function buildUnavailableArtifactPreviewItem(
  artifact: Artifact,
  timestamp: Date,
  unavailableReason: string,
): File {
  return {
    type: 'file',
    id: getArtifactPreviewItemId(artifact),
    messageId: NULL_MESSAGE_ID,
    name: artifact.displayPath,
    language: artifact.language,
    value: '',
    isStreaming: false,
    isUserEdited: false,
    readOnly: true,
    previewEligible: false,
    htmlPreview: undefined,
    timestamp,
    unavailableReason,
  }
}

export function useSyncOpenArtifactPreviews(
  artifacts: Artifact[],
  historyIsSettled = true,
  readableRefPaths: ReadonlySet<string> = EMPTY_READABLE_REF_PATHS,
  isInWorkbench = false,
  artifactWritesSettled = false,
) {
  const contentPreview = useOptionalArtifactContentPreview()
  const planCacheRef = useRef(new Map<File['id'], ArtifactHtmlPreviewCacheEntry>())
  const updateItem = contentPreview?.updateItem
  const openItem = contentPreview?.openItem
  const closeItems = contentPreview?.closeItems
  const closePreviewPane = contentPreview?.closePreviewPane

  useEffect(() => {
    const items = contentPreview?.items
    const allOpenItems = contentPreview?.allOpenItems
    if (!items || !allOpenItems || !updateItem || !openItem || !closeItems || !closePreviewPane) return

    const artifactsById = new Map(artifacts.map(artifact => [getArtifactPreviewItemId(artifact), artifact]))
    for (const id of planCacheRef.current.keys()) {
      if (!allOpenItems.includes(id) || !artifactsById.has(id)) planCacheRef.current.delete(id)
    }
    // Absence means removal only once history is settled. While a partial live
    // window still awaits its persisted replay, a missing artifact is merely
    // unloaded, and closing its tab cannot be undone once the replay lands.
    const artifactItemIdsToClose = historyIsSettled
      ? allOpenItems.filter(id => {
          if (!isArtifactPreviewItemId(id) || items.get(id)?.type !== 'file') return false
          const artifact = artifactsById.get(id)
          if (isInWorkbench) return artifact === undefined
          return artifact?.content === undefined && !readableRefPaths.has(artifact?.absolutePath ?? '')
        })
      : []

    if (artifactItemIdsToClose.length > 0) {
      const closingIds = new Set(artifactItemIdsToClose)
      const contentPreviewElement = contentPreview.contentPreviewRef.current
      const tabs = contentPreviewElement?.querySelectorAll<HTMLElement>('[role="tab"]')
      const renderedOpenItemIds = allOpenItems.filter(id => items.has(id))
      const focusedTabIndex =
        tabs && document.activeElement
          ? [...tabs].findIndex(tab => tab.parentElement?.contains(document.activeElement))
          : -1
      const focusIsInPreview =
        contentPreviewElement !== null &&
        document.activeElement !== null &&
        contentPreviewElement.contains(document.activeElement)
      // Only the selected item's tab panel counts as focus "within the selected
      // artifact". A persistent toolbar control (for example Collapse workbench)
      // sits in the preview but outside the panel, so associating it with the
      // selected item would move its focus away during reconciliation.
      const selectedTabPanel = contentPreviewElement?.querySelector<HTMLElement>('[role="tabpanel"]') ?? null
      const focusIsInSelectedTabPanel =
        selectedTabPanel !== null &&
        document.activeElement !== null &&
        selectedTabPanel.contains(document.activeElement)
      const focusedItemId =
        renderedOpenItemIds[focusedTabIndex] ?? (focusIsInSelectedTabPanel ? contentPreview.selectedItem : undefined)
      const focusedItemIndex = focusedItemId ? renderedOpenItemIds.indexOf(focusedItemId) : -1
      const survivingItemIds = renderedOpenItemIds.filter(id => !closingIds.has(id))

      // The nearest item that outlives this whole batch, searching forward from
      // the anchor and then backward. `closeItems` removes one id at a time and
      // picks each successor from its pre-batch list, so a neighbour it selects
      // can itself close later in the same batch and leave selection on a
      // removed item. Resolving against `closingIds` avoids that.
      const survivorNearIndex = (index: number) =>
        renderedOpenItemIds.slice(index + 1).find(id => !closingIds.has(id)) ??
        renderedOpenItemIds.slice(0, index).findLast(id => !closingIds.has(id))

      // Selecting a survivor and moving DOM focus are separate concerns.
      // Selection must always resolve to a surviving item when the selected tab
      // closes, whether or not focus is on the tabs: otherwise no tab stays
      // aria-selected and the tab panel's aria-labelledby points at a missing
      // id. This is anchored on the selected item, not on focus.
      const selectedItemId = contentPreview.selectedItem
      const nextItemId =
        selectedItemId !== undefined && closingIds.has(selectedItemId)
          ? survivorNearIndex(renderedOpenItemIds.indexOf(selectedItemId))
          : undefined

      // Moving DOM focus only happens when focus was inside a closing tab or its
      // editor. Stealing it otherwise would pull focus off a toolbar control
      // (for example Collapse workbench) during reconciliation. Prefer the newly
      // selected survivor so focus and selection stay together.
      if (focusedItemId && closingIds.has(focusedItemId) && focusedItemIndex !== -1) {
        const focusTargetId = nextItemId ?? survivorNearIndex(focusedItemIndex)
        if (focusTargetId) {
          const focusTabIndex = renderedOpenItemIds.indexOf(focusTargetId)
          tabs?.[focusTabIndex]?.focus()
        }
      }

      if (survivingItemIds.length === 0) {
        const returnFocusElementRef = contentPreview.returnFocusElementRef
        const activeElement = document.activeElement
        // A row the list is about to unmount is reconciled by the list itself, so
        // capturing it here would make the pane's restoration fight that repair.
        // A row that survives is still a valid return target.
        const focusedRowPath =
          activeElement instanceof HTMLElement
            ? activeElement.closest<HTMLElement>('[data-artifact-path]')?.getAttribute('data-artifact-path')
            : undefined
        const focusedRowWillUnmount =
          focusedRowPath != null && !artifacts.some(artifact => artifact.absolutePath === focusedRowPath)
        if (
          !focusIsInPreview &&
          !focusedRowWillUnmount &&
          activeElement instanceof HTMLElement &&
          activeElement !== document.body &&
          activeElement.isConnected
        ) {
          returnFocusElementRef.current = activeElement
        }
        closeItems(artifactItemIdsToClose)
        queueMicrotask(() => {
          const captured = returnFocusElementRef.current
          if (captured?.isConnected) return

          // No usable target: either nothing was captured because the focused row
          // was about to unmount, or what was captured has since been removed.
          // The list repairs that focus itself, and its effect has run by now, so
          // adopt wherever focus actually landed. Otherwise the pane restores to
          // its content-column fallback and overrides the repair.
          const repaired = document.activeElement
          returnFocusElementRef.current =
            repaired instanceof HTMLElement && repaired !== document.body && repaired.isConnected ? repaired : null
        })
        closePreviewPane()
      } else {
        closeItems(artifactItemIdsToClose)
      }
      if (nextItemId) openItem(nextItemId)
    }

    const focusSelectedArtifactTab = (id: File['id'], mode: 'all' | 'preview-controls' | 'iframe' = 'all') => {
      if (id !== contentPreview.selectedItem) return
      const activeElement = document.activeElement
      if (mode === 'iframe' && !(activeElement instanceof HTMLIFrameElement)) return
      const activeLabel =
        activeElement?.getAttribute('aria-label') ??
        activeElement
          ?.getAttribute('aria-labelledby')
          ?.split(/\s+/)
          .map(labelId => document.getElementById(labelId)?.textContent ?? '')
          .join(' ')
          .trim()
      if (
        mode === 'preview-controls' &&
        !(activeElement instanceof HTMLIFrameElement) &&
        activeLabel !== 'Reload preview page' &&
        !activeElement?.closest('[aria-label="File view"]')
      ) {
        return
      }
      const contentPreviewElement = contentPreview.contentPreviewRef.current
      const selectedTabPanel = contentPreviewElement?.querySelector<HTMLElement>('[role="tabpanel"]') ?? null
      if (document.activeElement !== null && selectedTabPanel?.contains(document.activeElement)) {
        const selectedTab = contentPreviewElement?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
        if (selectedTab) {
          selectedTab.focus()
          if (document.activeElement !== selectedTab) selectedTab.focus()
        }
      }
    }

    for (const artifact of artifacts) {
      const id = getArtifactPreviewItemId(artifact)
      if (!allOpenItems.includes(id)) continue

      const existingItem = items.get(id)
      if (!existingItem || existingItem.type !== 'file') continue
      if (artifact.content === undefined) {
        if (
          isInWorkbench &&
          historyIsSettled &&
          !readableRefPaths.has(artifact.absolutePath) &&
          existingItem.unavailableReason === undefined
        ) {
          focusSelectedArtifactTab(id)
          updateItem(buildUnavailableArtifactPreviewItem(artifact, new Date(), NO_PREVIEW_REASON))
          if (id === contentPreview.selectedItem && contentPreview.isContentPreviewOpen.value) {
            announce(NO_PREVIEW_REASON)
          }
        } else if (
          isInWorkbench &&
          existingItem.unavailableReason !== undefined &&
          (existingItem.name !== artifact.displayPath || existingItem.language !== artifact.language)
        ) {
          updateItem({
            ...existingItem,
            name: artifact.displayPath,
            language: artifact.language,
            previewEligible: false,
            htmlPreview: undefined,
          })
        }
        planCacheRef.current.delete(id)
        continue
      }

      let fields: Pick<File, 'previewEligible' | 'htmlPreview'>
      if (!artifactWritesSettled && existingItem.htmlPreview && existingItem.value === artifact.content) {
        fields = {previewEligible: existingItem.previewEligible, htmlPreview: existingItem.htmlPreview}
      } else if (isHtmlArtifact(artifact)) {
        const cached = cacheArtifactHtmlPreview(
          artifact,
          artifacts,
          artifactWritesSettled,
          planCacheRef.current.get(id),
        )
        planCacheRef.current.set(id, cached)
        fields = htmlPreviewFields(cached.plan, id)
      } else {
        planCacheRef.current.delete(id)
        fields = htmlPreviewFields(undefined, id)
      }
      if (
        existingItem.value === artifact.content &&
        existingItem.name === artifact.displayPath &&
        existingItem.language === artifact.language &&
        existingItem.readOnly === true &&
        existingItem.unavailableReason === undefined &&
        existingItem.previewEligible === fields.previewEligible &&
        existingItem.htmlPreview?.signature === fields.htmlPreview?.signature
      ) {
        continue
      }

      if (existingItem.previewEligible !== false && fields.previewEligible === false) {
        focusSelectedArtifactTab(id, 'preview-controls')
      } else if (existingItem.htmlPreview?.signature !== fields.htmlPreview?.signature) {
        focusSelectedArtifactTab(id, 'iframe')
      }
      updateItem({
        ...existingItem,
        value: artifact.content,
        name: artifact.displayPath,
        language: artifact.language,
        readOnly: true,
        ...fields,
        unavailableReason: undefined,
      })
    }
  }, [
    artifacts,
    artifactWritesSettled,
    historyIsSettled,
    isInWorkbench,
    readableRefPaths,
    contentPreview?.items,
    contentPreview?.allOpenItems,
    contentPreview?.selectedItem,
    contentPreview?.isContentPreviewOpen,
    contentPreview?.contentPreviewRef,
    contentPreview?.returnFocusElementRef,
    updateItem,
    openItem,
    closeItems,
    closePreviewPane,
  ])
}
